import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTable } from "@/lib/store";
import { Avatar } from "@/components/kit";
import { Mark } from "@/components/Logo";
import { Assistant } from "@/components/Assistant";
import {
  ChevronDown, LogOut, Lock, Menu, X, PanelLeftClose, PanelLeftOpen, Sparkles,
  Home as HomeIcon, CalendarDays, Target, ClipboardList,
  Users, IndianRupee, Settings2, FolderOpen, History, ScrollText,
  Trophy,
} from "lucide-react";

const NAV = [
  ["Today", "/", HomeIcon, "Today"],
  ["Forum Cup", "/cup", Trophy, "Today"],
  ["Chapters", "/chapters", CalendarDays, "The Forum"],
  ["The Ledger", "/ledger", ClipboardList, "The Forum"],
  ["Goals", "/goals", Target, "The Forum"],
  ["Fines", "/fines", IndianRupee, "The Forum"],
  ["Members", "/members", Users, "The Record"],
  ["Drive", "/drive", FolderOpen, "The Record"],
  ["The Charter", "/charter", ScrollText, "The Record"],
  ["History", "/history", History, "The Record"],
];

const GROUPS = ["Today", "The Forum", "The Record", "System"];
const testId = (label) => label.toLowerCase().replace(/\s/g, "-");

const Monogram = ({ size = 30 }) => <Mark size={size} onDark />;

const YearSwitcher = () => {
  const { years, yearId, setYearId } = useTable();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="year-switcher" onClick={() => setOpen((o) => !o)} className="chip !py-1.5">
        <span className="numeral !text-[12px]">{yearId || "—"}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 card-t !p-1.5 z-40 min-w-[170px] fade-in">
          {years.map((y) => (
            <button
              key={y.id}
              data-testid={`year-option-${y.id}`}
              onClick={() => { setYearId(y.id); setOpen(false); }}
              className="flex w-full items-center justify-between gap-3 px-2.5 py-2 rounded-md hover:bg-[var(--paper-deep)] transition-colors"
            >
              <span className="numeral text-[13px]">{y.id}</span>
              <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--ink-mute)" }}>
                {y.status}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const NavItem = ({ label, to, Icon, collapsed, onNavigate, idPrefix = "nav" }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`${idPrefix}-${testId(label)}`}
    onClick={onNavigate}
    title={collapsed ? label : undefined}
    className="relative flex items-center gap-3 mx-2 px-3 py-2.5 rounded-md text-[13.5px]"
    style={({ isActive }) => ({
      background: isActive ? "#1e293b" : "transparent",
      color: isActive ? "#ffffff" : "#94a3b8",
      fontWeight: isActive ? 500 : 400,
      justifyContent: collapsed ? "center" : "flex-start",
      transition: "background-color .2s var(--ease), color .2s var(--ease)",
    })}
  >
    {({ isActive }) => (
      <>
        {isActive && (
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r"
            style={{ width: 3, height: 20, background: "var(--brass-bright)" }}
          />
        )}
        <Icon size={16} className="shrink-0" />
        {!collapsed && <span className="truncate">{label}</span>}
      </>
    )}
  </NavLink>
);

const SidebarBody = ({ nav, collapsed, onNavigate, idPrefix }) => (
  <nav className="flex-1 overflow-y-auto no-scrollbar py-4">
    {GROUPS.map((g) => {
      const items = nav.filter(([, , , group]) => group === g);
      if (!items.length) return null;
      return (
        <div key={g} className="mb-5">
          {!collapsed && (
            <div className="px-5 mb-1.5 text-[9px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#64748b" }}>
              {g}
            </div>
          )}
          {items.map(([label, to, Icon]) => (
            <NavItem
              key={to}
              label={label}
              to={to}
              Icon={Icon}
              collapsed={collapsed}
              onNavigate={onNavigate}
              idPrefix={idPrefix}
            />
          ))}
        </div>
      );
    })}
  </nav>
);

export const Shell = ({ children }) => {
  const { me, isArchive, signOut } = useTable();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(localStorage.getItem("table_rail") === "1");
  const [drawer, setDrawer] = useState(false);
  const [assistant, setAssistant] = useState(false);
  const nav = me?.isAdmin ? [...NAV, ["Admin", "/admin", Settings2, "System"]] : NAV;

  useEffect(() => setDrawer(false), [loc.pathname]);
  useEffect(() => localStorage.setItem("table_rail", collapsed ? "1" : "0"), [collapsed]);

  const Foot = ({ collapsed: c }) => (
    <div className="px-5 py-4 flex items-start gap-2" style={{ borderTop: "1px solid #1e293b", color: "#64748b" }}>
      <Lock size={11} className="mt-0.5 shrink-0" />
      {!c && <span className="text-[10px] leading-[1.6]">What happens at the table, stays at the table</span>}
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* desktop rail */}
      <aside
        className="hidden lg:flex flex-col shrink-0 sticky top-0 h-screen panel-ink no-print"
        style={{ width: collapsed ? 68 : 256, transition: "width .22s var(--ease)" }}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2.5 px-4 h-16 shrink-0" style={{ borderBottom: "1px solid #1e293b" }}>
          <Monogram />
          {!collapsed && (
            <span className="text-[12px] tracking-[0.16em] uppercase font-semibold" style={{ color: "#e2e8f0" }}>
              The Table
            </span>
          )}
        </div>
        <SidebarBody nav={nav} collapsed={collapsed} idPrefix="nav" />
        <Foot collapsed={collapsed} />
      </aside>

      {/* mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-[90] flex" data-testid="nav-drawer">
          <div className="absolute inset-0" style={{ background: "rgba(15,23,42,.45)" }} onClick={() => setDrawer(false)} />
          <aside className="relative panel-ink w-[264px] h-full flex flex-col fade-in">
            <div className="flex items-center gap-2.5 px-4 h-16 shrink-0" style={{ borderBottom: "1px solid #1e293b" }}>
              <Monogram />
              <span className="text-[12px] tracking-[0.16em] uppercase font-semibold" style={{ color: "#e2e8f0" }}>
                The Table
              </span>
              <button
                className="ml-auto p-1.5 rounded-md"
                style={{ color: "#94a3b8" }}
                data-testid="drawer-close"
                onClick={() => setDrawer(false)}
              >
                <X size={16} />
              </button>
            </div>
            <SidebarBody nav={nav} collapsed={false} idPrefix="nav-m" onNavigate={() => setDrawer(false)} />
            <Foot collapsed={false} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header
          className="sticky top-0 z-30 h-16 flex items-center gap-3 px-4 sm:px-6 shrink-0 no-print"
          style={{ background: "var(--card)", borderBottom: "1px solid var(--hairline)" }}
        >
          <button
            className="lg:hidden btn-quiet !min-h-0 !px-2 !py-2"
            data-testid="nav-toggle-mobile"
            aria-label="Open navigation"
            onClick={() => setDrawer(true)}
          >
            <Menu size={16} />
          </button>
          <button
            className="hidden lg:inline-flex btn-quiet !min-h-0 !px-2 !py-2"
            data-testid="nav-toggle"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>

          <span className="lg:hidden text-[12px] tracking-[0.16em] uppercase font-semibold" style={{ color: "var(--ink)" }}>
            The Table
          </span>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button className="btn-quiet !min-h-0 !py-2" data-testid="assistant-open" onClick={() => setAssistant(true)}>
              <Sparkles size={14} style={{ color: "var(--brass-bright)" }} />
              <span className="hidden sm:inline">Ask</span>
            </button>
            <YearSwitcher />
            <div className="hidden sm:flex items-center gap-2.5 pl-3" style={{ borderLeft: "1px solid var(--hairline)" }}>
              <div className="text-right leading-tight">
                <div className="text-[13px] font-medium" style={{ color: "var(--ink)" }}>{me?.name}</div>
                <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--ink-mute)" }}>{me?.role}</div>
              </div>
              <Avatar name={me?.name || "?"} color={me?.member?.avatarColor} size={30} />
            </div>
            <button data-testid="sign-out" onClick={signOut} className="btn-quiet !min-h-0 !px-2 !py-2" aria-label="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        </header>

        {isArchive && (
          <div
            data-testid="archive-banner"
            className="text-center py-2 text-[12px] font-medium"
            style={{ background: "var(--amber-wash)", color: "#92400e", borderBottom: "1px solid #fde68a" }}
          >
            Archive — read-only
          </div>
        )}

        <main key={loc.pathname} className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1180px] w-full fade-in">
          {children}
        </main>
      </div>

      <Assistant open={assistant} onClose={() => setAssistant(false)} />
    </div>
  );
};
